// The pipeline (the sales pipeline's working page): every lead, as a board with a column per open stage or as a table.
// New lead, Import and Templates are in its header. The filters — stage, city, priority, owner, do-not-contact, search and
// sort — live in the address, so a filtered view can be linked, and the api does the filtering (`GET /v1/hq/leads`).
//
// The board asks the api once per column (the stage filter is the column), so each column pages on its own. A card's menu
// moves it to another stage; moving to Lost asks why first. Clicking a card opens the lead.

import { canMoveLeadStage } from '@clavian/domain/leads';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Chip, EmptyState, Menu, SegmentedControl, SelectField, Skeleton, Table, useToast, VisuallyHidden, type MenuEntry, type TableColumn } from '@clavian/ui-web';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { PROBLEM, problemCode } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { LoadFailure } from '../../shell/LoadFailure';
import { Icon, NavIcon } from '../../shell/nav-icons';
import { useNow } from '../../floor/live';
import { SearchField, TableSkeleton, Toolbar, ToolbarSpring } from '../../ui';
import { NotPermitted, RequirePlatformPermission } from '../access';
import { HQ_PATHS } from '../paths';
import { HQ_LEADS } from '../sections';
import { ImportLeadsDialog } from './ImportLeadsDialog';
import { CLOSED_STAGES, LEAD_STAGE_ORDER, LeadPriorityPill, LeadStagePill, OPEN_STAGES, ratingText } from './labels';
import { LeadFormDialog } from './LeadFormDialog';
import { LeadsHeader } from './LeadsHeader';
import { LostReasonDialog } from './LostReasonDialog';
import { TemplatesDialog } from './TemplatesDialog';
import {
  leadFiltersFrom,
  leadParams,
  LEAD_PRIORITY_FILTERS,
  LEAD_SORTS,
  useChangeLeadStage,
  useLeadAssignees,
  useLeadCities,
  useLeadFunnel,
  useLeadList,
  type LeadFilters,
  type LeadSort,
  type LeadStageFilter,
  type LeadSummary,
} from './queries';
import '../hq.css';

export function PipelinePage(): ReactNode {
  return (
    <RequirePlatformPermission permission={HQ_LEADS}>
      <Pipeline />
    </RequirePlatformPermission>
  );
}

type View = 'board' | 'table';

const VIEW_KEY = 'clavian.hq.leads.view';
/** The cities shown as chips; the rest go in a select. */
const TOP_CITIES = 4;
/** How often a follow-up's "overdue" is re-read: a minute. */
const NOW_TICK_MS = 60_000;
/** The select's stand-in for "no choice": an option cannot have an empty value. */
const ANY = 'any';

function readView(): View {
  try {
    return window.localStorage.getItem(VIEW_KEY) === 'table' ? 'table' : 'board';
  } catch {
    return 'board';
  }
}

function rememberView(view: View): void {
  try {
    window.localStorage.setItem(VIEW_KEY, view);
  } catch {
    // Private window or blocked storage: the choice is simply not kept.
  }
}

function Pipeline(): ReactNode {
  const { t } = useTranslation('hq');
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const filters = useMemo(() => leadFiltersFrom(search), [search]);
  const navigate = useNavigate();
  const [view, setViewState] = useState<View>(readView);
  const [formOpen, setFormOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [lostLead, setLostLead] = useState<LeadSummary | undefined>(undefined);
  const funnel = useLeadFunnel();

  const setView = (next: View) => {
    setViewState(next);
    rememberView(next);
  };

  /** Change some filters in the address: an undefined value removes one. */
  const setFilters = (patch: { [K in keyof LeadFilters]?: LeadFilters[K] | undefined }) => {
    const next: Record<string, unknown> = { ...filters, ...patch };
    for (const key of Object.keys(next)) if (next[key] === undefined) delete next[key];
    void navigate({ to: HQ_PATHS.pipeline, search: next as never, replace: true });
  };

  const hasFilters = Object.keys(filters).length > 0;
  const noLeadsAtAll = funnel.data !== undefined && funnel.data.totals.leads === 0;

  const actions = (
    <>
      <Button variant="ghost" onClick={() => setTemplatesOpen(true)}>
        {t('leads.pipeline.templates')}
      </Button>
      <Button variant="secondary" onClick={() => setImportOpen(true)}>
        {t('leads.pipeline.importLeads')}
      </Button>
      <Button variant="primary" onClick={() => setFormOpen(true)}>
        {t('leads.pipeline.newLead')}
      </Button>
    </>
  );

  return (
    <LeadsHeader current="pipeline" actions={actions}>
      {noLeadsAtAll && !hasFilters ? (
        <EmptyState
          icon={<NavIcon name="leads" />}
          title={t('leads.pipeline.emptyTitle')}
          description={t('leads.pipeline.empty')}
          action={
            <div className="console-hq-actions">
              <Button variant="secondary" onClick={() => setImportOpen(true)}>
                {t('leads.pipeline.importLeads')}
              </Button>
              <Button variant="primary" onClick={() => setFormOpen(true)}>
                {t('leads.pipeline.newLead')}
              </Button>
            </div>
          }
        />
      ) : (
        <>
          <FilterBar filters={filters} setFilters={setFilters} view={view} setView={setView} hasFilters={hasFilters} />
          {view === 'board' ? <Board filters={filters} setFilters={setFilters} onLost={setLostLead} /> : <LeadTable filters={filters} onLost={setLostLead} />}
        </>
      )}
      <LeadFormDialog open={formOpen} onOpenChange={setFormOpen} />
      <ImportLeadsDialog open={importOpen} onOpenChange={setImportOpen} />
      <TemplatesDialog open={templatesOpen} onOpenChange={setTemplatesOpen} />
      <LostReasonDialog lead={lostLead} onClose={() => setLostLead(undefined)} />
    </LeadsHeader>
  );
}

// -------------------------------------------------------------------------------------------------
// Filters
// -------------------------------------------------------------------------------------------------

type SetFilters = (patch: { [K in keyof LeadFilters]?: LeadFilters[K] | undefined }) => void;

function FilterBar({
  filters,
  setFilters,
  view,
  setView,
  hasFilters,
}: {
  readonly filters: LeadFilters;
  readonly setFilters: SetFilters;
  readonly view: View;
  readonly setView: (view: View) => void;
  readonly hasFilters: boolean;
}): ReactNode {
  const { t } = useTranslation('hq');
  const cities = useLeadCities();
  const assignees = useLeadAssignees();
  const [text, setText] = useState(filters.q ?? '');

  // The search goes to the api a moment after typing stops, and the address keeps it.
  useEffect(() => {
    const next = text.trim() === '' ? undefined : text.trim();
    if (next === filters.q) return;
    const timer = window.setTimeout(() => setFilters({ q: next }), 300);
    return () => window.clearTimeout(timer);
    // `setFilters` is a new function each render; the address (`filters.q`) is what the effect follows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, filters.q]);

  // The box follows the address when something else changes it (Clear filters, a link, back): never while typing, as the address
  // only changes once typing has stopped.
  const [seenQ, setSeenQ] = useState(filters.q);
  if (seenQ !== filters.q) {
    setSeenQ(filters.q);
    if ((text.trim() === '' ? undefined : text.trim()) !== filters.q) setText(filters.q ?? '');
  }

  const allCities = cities.data?.data ?? [];
  const top = allCities.slice(0, TOP_CITIES);
  const rest = allCities.filter((one) => !top.some((chip) => chip.city === one.city) && one.city !== filters.city);
  // The city in the address is always a chip, so it can be switched off even when it is not a big one.
  const chipCities = filters.city !== undefined && !top.some((one) => one.city === filters.city) ? [...top, { city: filters.city, count: allCities.find((one) => one.city === filters.city)?.count ?? 0 }] : top;

  const stageChip = (stage: LeadStageFilter, label: string) => (
    <Chip key={stage} selected={filters.stage === stage} onSelectedChange={(on) => setFilters({ stage: on ? stage : undefined })}>
      {label}
    </Chip>
  );

  return (
    <div className="console-hq-leadfilters">
      <Toolbar>
        <SearchField label={t('leads.pipeline.search')} value={text} onChange={(event) => setText(event.target.value)} />
        <SelectField
          label={t('leads.pipeline.filters.sort')}
          className="console-hq-leadfilters__sort"
          options={LEAD_SORTS.map((one) => ({ value: one, label: t(`leads.pipeline.sort.${one}`) }))}
          value={filters.sort ?? 'followUp'}
          onValueChange={(value) => setFilters({ sort: value === 'followUp' ? undefined : (value as LeadSort) })}
        />
        <ToolbarSpring />
        <SegmentedControl<View>
          size="sm"
          label={t('leads.pipeline.view.label')}
          value={view}
          onValueChange={setView}
          options={[
            { value: 'board', label: t('leads.pipeline.view.board'), icon: <Icon name="grid" /> },
            { value: 'table', label: t('leads.pipeline.view.table'), icon: <Icon name="list" /> },
          ]}
        />
      </Toolbar>
      <div className="console-hq-chips" role="group" aria-label={t('leads.pipeline.filters.stage')}>
        {stageChip('open', t('leads.pipeline.stageOpen'))}
        {LEAD_STAGE_ORDER.map((stage) => stageChip(stage, t(`leads.stage.${stage}`)))}
      </div>
      <div className="console-hq-chips" role="group" aria-label={t('leads.pipeline.filters.label')}>
        {chipCities.map((one) => (
          <Chip key={one.city} selected={filters.city === one.city} count={one.count} onSelectedChange={(on) => setFilters({ city: on ? one.city : undefined })}>
            {one.city}
          </Chip>
        ))}
        {rest.length > 0 ? (
          <SelectField
            label={t('leads.pipeline.moreCities')}
            className="console-hq-leadfilters__select"
            options={rest.map((one) => ({ value: one.city, label: `${one.city} (${one.count})` }))}
            placeholder={t('leads.pipeline.anyCity')}
            onValueChange={(value) => setFilters({ city: value })}
          />
        ) : null}
        {LEAD_PRIORITY_FILTERS.map((priority) => (
          <Chip key={priority} selected={filters.priority === priority} onSelectedChange={(on) => setFilters({ priority: on ? priority : undefined })}>
            {t('leads.pipeline.priorityChip', { priority: t(`leads.priority.${priority}`) })}
          </Chip>
        ))}
        <Chip selected={filters.doNotContact === 'true'} onSelectedChange={(on) => setFilters({ doNotContact: on ? 'true' : undefined })}>
          {t('leads.pipeline.doNotContact')}
        </Chip>
        {(assignees.data?.data.length ?? 0) > 0 ? (
          <SelectField
            label={t('leads.pipeline.filters.owner')}
            className="console-hq-leadfilters__select"
            options={[{ value: ANY, label: t('leads.pipeline.anyOwner') }, ...(assignees.data?.data ?? []).map((one) => ({ value: one.id, label: one.name }))]}
            value={filters.ownerId ?? ANY}
            onValueChange={(value) => setFilters({ ownerId: value === ANY ? undefined : value })}
          />
        ) : null}
        {hasFilters ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setText('');
              setFilters({ stage: undefined, city: undefined, priority: undefined, ownerId: undefined, doNotContact: undefined, q: undefined, sort: undefined });
            }}
          >
            {t('leads.pipeline.clearFilters')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------------------------------
// Moving a lead
// -------------------------------------------------------------------------------------------------

/** The stages a lead's menu offers: the ones it can move to, with Lost asking for a reason. */
function useMoveEntries(onLost: (lead: LeadSummary) => void): (lead: LeadSummary) => MenuEntry[] {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const change = useChangeLeadStage();
  return (lead) => {
    const entries: MenuEntry[] = [{ kind: 'label', id: 'move', label: t('leads.pipeline.moveTo') }];
    for (const stage of LEAD_STAGE_ORDER) {
      if (!canMoveLeadStage(lead.stage, stage)) continue;
      entries.push({
        id: stage,
        label: t(`leads.stage.${stage}`),
        onSelect: () => {
          if (stage === 'lost') {
            onLost(lead);
            return;
          }
          change.mutate(
            { leadId: lead.id, body: { stage } },
            {
              onSuccess: () => show({ tone: 'ok', title: t('leads.pipeline.moved', { name: lead.businessName, stage: t(`leads.stage.${stage}`) }) }),
              onError: (error) => show({ tone: 'danger', title: problems.text(error, { fallback: t('leads.pipeline.moveFailed') }) }),
            },
          );
        },
      });
    }
    return entries;
  };
}

function LeadMenu({ lead, entries }: { readonly lead: LeadSummary; readonly entries: readonly MenuEntry[] }): ReactNode {
  const { t } = useTranslation('hq');
  return (
    <Menu
      items={entries}
      trigger={<Button variant="ghost" size="sm" className="console-row-actions" icon={<Icon name="more" />} label={t('leads.pipeline.cardMenu', { name: lead.businessName })} />}
    />
  );
}

// -------------------------------------------------------------------------------------------------
// Board
// -------------------------------------------------------------------------------------------------

function Board({ filters, setFilters, onLost }: { readonly filters: LeadFilters; readonly setFilters: SetFilters; readonly onLost: (lead: LeadSummary) => void }): ReactNode {
  const { t } = useTranslation('hq');
  const funnel = useLeadFunnel();
  const moveEntries = useMoveEntries(onLost);
  // One column when a stage is picked; every open stage otherwise.
  const picked = filters.stage === undefined || filters.stage === 'open' ? undefined : filters.stage;
  const columns = picked === undefined ? OPEN_STAGES : [picked];
  const { stage: _stage, ...rest } = filters;
  const counts = new Map((funnel.data?.byStage ?? []).map((row) => [row.stage, row.count] as const));

  return (
    <div className="console-lead-board-frame">
      <div className="console-lead-board" role="list" aria-label={t('leads.pipeline.board')}>
        {columns.map((stage) => (
          <BoardColumn key={stage} stage={stage} filters={rest} moveEntries={moveEntries} />
        ))}
      </div>
      {picked === undefined ? (
        <div className="console-lead-strip" role="group" aria-label={t('leads.pipeline.parkedStrip')}>
          <span className="console-lead-strip__label">{t('leads.pipeline.parkedStrip')}</span>
          {CLOSED_STAGES.map((stage) => (
            <Button key={stage} variant="ghost" size="sm" onClick={() => setFilters({ stage })}>
              {t(`leads.stage.${stage}`)} <span className="console-num console-subtle">{counts.get(stage) ?? 0}</span>
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function BoardColumn({ stage, filters, moveEntries }: { readonly stage: LeadStageFilter; readonly filters: LeadFilters; readonly moveEntries: (lead: LeadSummary) => MenuEntry[] }): ReactNode {
  const { t } = useTranslation('hq');
  const list = useLeadList({ ...filters, stage });
  const now = useNow(NOW_TICK_MS);
  const rows = list.data?.pages.flatMap((page) => page.data) ?? [];
  const heading = t(`leads.stage.${stage}`);

  let body: ReactNode;
  if (list.data === undefined) {
    if (list.isError && problemCode(list.error) === PROBLEM.forbidden) return <NotPermitted />;
    body = list.isError ? (
      <LoadFailure error={list.error} message={t('leads.unavailable')} onRetry={() => void list.refetch()} />
    ) : (
      <div className="console-lead-column__cards" aria-busy="true">
        {[0, 1, 2].map((index) => (
          <Skeleton key={index} height="5.5rem" />
        ))}
      </div>
    );
  } else {
    body = (
      <div className="console-lead-column__cards">
        {rows.length === 0 ? <p className="console-subtle console-lead-column__empty">{t('leads.pipeline.column.empty')}</p> : rows.map((lead) => <LeadCard key={lead.id} lead={lead} entries={moveEntries(lead)} now={now} />)}
        {list.hasNextPage ? (
          <Button variant="ghost" size="sm" busy={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
            {t('leads.pipeline.column.more')}
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <section className="console-lead-column" role="listitem" aria-label={heading}>
      <header className="console-lead-column__header">
        <h2 className="console-lead-column__title">{heading}</h2>
        <span className="console-num console-subtle">{list.data === undefined ? '' : String(list.data.pages[0]?.total ?? rows.length)}</span>
      </header>
      {body}
    </section>
  );
}

/** When a follow-up is: relative, with whether it has passed. */
function useFollowUp(lead: LeadSummary, now: number): { readonly text: string; readonly overdue: boolean } | undefined {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  if (lead.nextFollowUpAt === null) return undefined;
  const overdue = new Date(lead.nextFollowUpAt).getTime() < now;
  const when = format.relative(lead.nextFollowUpAt);
  return { text: t(overdue ? 'leads.pipeline.followUpOverdue' : 'leads.pipeline.followUp', { when }), overdue };
}

function LeadCard({ lead, entries, now }: { readonly lead: LeadSummary; readonly entries: readonly MenuEntry[]; readonly now: number }): ReactNode {
  const { t } = useTranslation('hq');
  const followUp = useFollowUp(lead, now);
  const place = [lead.city, lead.area].filter((part): part is string => part !== null && part !== '').join(' · ');
  return (
    <article className="console-lead-card" aria-label={lead.businessName}>
      <div className="console-lead-card__head">
        <Link to={HQ_PATHS.lead} params={leadParams(lead.id)} className="console-lead-card__name">
          {lead.businessName}
        </Link>
        <LeadMenu lead={lead} entries={entries} />
      </div>
      {place === '' ? null : <p className="console-lead-card__place">{place}</p>}
      <div className="console-lead-card__badges">
        <LeadPriorityPill priority={lead.priority} />
        {lead.googleRatingTenths === null ? null : (
          <span className="console-lead-card__rating console-num">
            {ratingText(lead.googleRatingTenths)}
            {lead.googleReviewCount === null ? null : <span className="console-subtle"> ({t('leads.pipeline.reviews', { count: lead.googleReviewCount })})</span>}
          </span>
        )}
        {lead.phoneMobile && !lead.doNotContact ? (
          <span className="console-lead-card__wa" title={t('leads.pipeline.whatsAppOk')} role="img" aria-label={t('leads.pipeline.whatsAppOk')}>
            <Icon name="message" />
          </span>
        ) : null}
        {lead.doNotContact ? (
          <Badge tone="danger" dot>
            {t('leads.pipeline.dnc')}
          </Badge>
        ) : null}
      </div>
      {followUp === undefined ? null : <p className={followUp.overdue ? 'console-lead-card__followup console-lead-card__followup--overdue' : 'console-lead-card__followup'}>{followUp.text}</p>}
    </article>
  );
}

// -------------------------------------------------------------------------------------------------
// Table
// -------------------------------------------------------------------------------------------------

function FollowUpCell({ lead, now }: { readonly lead: LeadSummary; readonly now: number }): ReactNode {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  if (lead.nextFollowUpAt === null) return <span className="console-subtle">{t('leads.pipeline.none')}</span>;
  const overdue = new Date(lead.nextFollowUpAt).getTime() < now;
  return <span className={overdue ? 'console-lead-card__followup--overdue' : undefined}>{format.relative(lead.nextFollowUpAt)}</span>;
}

function LeadTable({ filters, onLost }: { readonly filters: LeadFilters; readonly onLost: (lead: LeadSummary) => void }): ReactNode {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  const list = useLeadList(filters);
  const now = useNow(NOW_TICK_MS);
  const moveEntries = useMoveEntries(onLost);
  const none = <span className="console-subtle">{t('leads.pipeline.none')}</span>;

  const columns: readonly TableColumn<LeadSummary>[] = [
    {
      key: 'business',
      header: t('leads.pipeline.columns.business'),
      cell: (lead) => (
        <span className="console-hq-name">
          <Link to={HQ_PATHS.lead} params={leadParams(lead.id)} className="console-hq-name__link">
            {lead.businessName}
          </Link>
          {lead.doNotContact ? <Badge tone="danger">{t('leads.pipeline.dnc')}</Badge> : null}
        </span>
      ),
    },
    { key: 'city', header: t('leads.pipeline.columns.city'), cell: (lead) => lead.city ?? none },
    { key: 'area', header: t('leads.pipeline.columns.area'), cell: (lead) => lead.area ?? none },
    { key: 'stage', header: t('leads.pipeline.columns.stage'), cell: (lead) => <LeadStagePill stage={lead.stage} /> },
    { key: 'priority', header: t('leads.pipeline.columns.priority'), cell: (lead) => <LeadPriorityPill priority={lead.priority} /> },
    { key: 'rating', header: t('leads.pipeline.columns.rating'), numeric: true, cell: (lead) => (lead.googleRatingTenths === null ? none : ratingText(lead.googleRatingTenths)) },
    { key: 'reviews', header: t('leads.pipeline.columns.reviews'), numeric: true, cell: (lead) => (lead.googleReviewCount === null ? none : lead.googleReviewCount) },
    { key: 'followUp', header: t('leads.pipeline.columns.nextFollowUp'), cell: (lead) => <FollowUpCell lead={lead} now={now} /> },
    { key: 'lastContacted', header: t('leads.pipeline.columns.lastContacted'), cell: (lead) => (lead.lastContactedAt === null ? <span className="console-subtle">{t('leads.pipeline.never')}</span> : format.relative(lead.lastContactedAt)) },
    { key: 'owner', header: t('leads.pipeline.columns.owner'), cell: (lead) => lead.ownerName ?? none },
    { key: 'phone', header: t('leads.pipeline.columns.phone'), cell: (lead) => (lead.phone === null ? none : <span className="console-hq-nowrap console-num">{lead.phone}</span>) },
    { key: 'actions', header: <VisuallyHidden>{t('leads.pipeline.moveTo')}</VisuallyHidden>, cell: (lead) => <LeadMenu lead={lead} entries={moveEntries(lead)} /> },
  ];

  if (list.data === undefined) {
    if (list.isError && problemCode(list.error) === PROBLEM.forbidden) return <NotPermitted />;
    return list.isError ? <LoadFailure error={list.error} message={t('leads.unavailable')} onRetry={() => void list.refetch()} /> : <TableSkeleton />;
  }
  const rows = list.data.pages.flatMap((page) => page.data);
  return (
    <>
      <div className="console-table-card">
        <Table
          caption={t('leads.pipeline.caption')}
          captionHidden
          columns={columns}
          rows={rows}
          rowKey={(lead) => lead.id}
          rowTone={(lead) => (lead.nextFollowUpAt !== null && new Date(lead.nextFollowUpAt).getTime() < now && lead.stage !== 'won' && lead.stage !== 'lost' ? 'warn' : undefined)}
          empty={<EmptyState title={t('leads.pipeline.noMatchTitle')} description={t('leads.pipeline.noMatch')} />}
        />
      </div>
      {list.hasNextPage ? (
        <Button className="console-hq-more" variant="ghost" busy={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
          {t('leads.pipeline.more')}
        </Button>
      ) : null}
      {list.isFetchNextPageError ? <LoadFailure error={list.error} onRetry={() => void list.fetchNextPage()} /> : null}
    </>
  );
}
